import { useCallback, useEffect, useState } from 'react';
import { Form, Input, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getSpouseTitle } from './constants';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors } from 'features/auth';

export function FamilyAppHouseholdStructure({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const dispatch = useDispatch();
  const stepConfig = useAtomValue(stepConfigAtom);
  const currentUser = useSelector(selectors.selectUser);

  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Form state
  const [form] = Form.useForm();

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        // Other adult
        const additionalParentId = application?.additionalParents?.[0]?.id;
        const secondAdultUpdates = {
          ...values,
          maritalStatus: application?.applicant?.maritalStatus,
          partnerStatus: application?.applicant?.partnerStatus,
        };
        const updated2ndAdultObject = await familyService.addUpdateAdult(
          household?.id,
          additionalParentId ?? null,
          additionalParentId ? { ...application?.additionalParents?.[0], ...secondAdultUpdates } : [secondAdultUpdates],
          !isCompletedBySponsor,
        );
        // Assign the parent to application
        if (additionalParentId === undefined || additionalParentId === null) {
          await familyService.addSecondParent(application?.id, {
            ...values,
            id: updated2ndAdultObject?.id || updated2ndAdultObject?.[0]?.id,
          });
        }

        const _newApplication = await familyService.saveApplication({
          ...application,
        });
        setApplication(_newApplication);

        if (!isCompletedBySponsor) {
          dispatch(actions.updateUser({ firstName: values.firstName, lastName: values.lastName }));
        }

        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, dispatch, household?.id, isCompletedBySponsor, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    form.setFieldsValue({
      firstName:
        application?.additionalParents?.[0]?.firstName || (!isCompletedBySponsor ? currentUser?.firstName : null),
      lastName: application?.additionalParents?.[0]?.lastName || (!isCompletedBySponsor ? currentUser?.lastName : null),
    });
  }, [application, currentUser, form, isCompletedBySponsor]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Household Structure" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? application?.applicant?.partnerStatus === ADULT_MARITAL_STATUS_OPTIONS.POA
                  ? "Now we'll need information about the person with Power of Attorney"
                  : application?.applicant?.maritalStatus === ADULT_MARITAL_STATUS_OPTIONS.SINGLE
                  ? `Now we'll need information about your ${getSpouseTitle(application)}`
                  : application?.applicant?.maritalStatus === ADULT_MARITAL_STATUS_OPTIONS.SEPARATED
                  ? "Now we'll need information about your child's other parent"
                  : "Now we'll need information about your spouse"
                : 'Now we need your information'
            }
            className="mt-12 mb-8"
          />

          <div className="flex flex-col md:flex-row flex-wrap space-y-4 md:space-y-0 space-x-0 md:space-x-4">
            <div className="w-full md:w-[calc(50%-0.5rem)]">
              <Form.Item
                name="firstName"
                label={'First name'}
                rules={[
                  {
                    required: true,
                    message: 'First name is required',
                  },
                ]}
                className="mb-3"
              >
                <Input />
              </Form.Item>
            </div>

            <div className="w-full md:w-[calc(50%-0.5rem)]">
              <Form.Item
                name="lastName"
                label={'Last name'}
                rules={[
                  {
                    required: true,
                    message: 'Last name is required',
                  },
                ]}
                className="mb-3"
              >
                <Input />
              </Form.Item>
            </div>
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

// [ Married, Separated, CoParent, DivorcedSharedCustody, DivorcedSoleCustody, POA ]
export const ADULT_MARITAL_STATUS_OPTIONS = {
  MARRIED: 'Married',
  SINGLE: 'Single',
  SEPARATED: 'Separated',
  CO_PARENT: 'CoParent',
  DIVORCED_SHARED_CUSTODY: 'DivorcedSharedCustody',
  // DIVORCED_SOLE_CUSTODY: 'DivorcedSoleCustody',
  POA: 'POA',
};
